import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';

import { useStateCallback } from 'site-modules/shared/hooks/use-state-callback';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { VehicleImage } from 'site-modules/shared/components/upper-funnel/vehicle-image/vehicle-image';
import { CardHeader } from 'site-modules/upper-funnel/components/type/rankings-vehicle-card/card-details/card-header';
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { COMPARE_VEHICLE_TRAY_ID } from 'site-modules/shared/components/compare-vehicle-tray/compare-vehicle-tray';
import { getOrdinalNumber } from 'site-modules/upper-funnel/components/type/rankings-vehicle-card/get-ordinal-number';
import { MAX_CARS_TO_COMPARE } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import hondaLogo from 'client/images/ads/top-rated-2025/honda-logo.png';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { FeatureFlag } from 'site-modules/shared/components/feature-flag/feature-flag';
import { LeaseDealsHiddenGemsLink } from 'site-modules/upper-funnel/components/type/lease-deals-hidden-gems-link/lease-deals-hidden-gems-link';
import { CardDetails } from './card-details/card-details';

import './rankings-vehicle-card.scss';

const ETR_ADS_PROPS = {
  // desktop
  false: { adName: 'rankingbutton', md: true, lg: true, xl: true },
  // mobile
  true: { adName: 'mrankingbutton', xs: true, sm: true },
};

const HONDA_CLICK_TAG = {
  false:
    'https://ad.doubleclick.net/ddm/trackclk/N8821.2990.EDMUNDS.COMINC/B33067446.411506449;dc_trk_aid=605163121;dc_trk_cid=105363203;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
  true: 'https://ad.doubleclick.net/ddm/trackclk/N8821.2990.EDMUNDS.COMINC/B33067446.411669350;dc_trk_aid=605163121;dc_trk_cid=105363203;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
};

export function RankingsVehicleCard({
  onSelectCar,
  selected,
  selectedCarsNumber,
  vehicle,
  showRank,
  showEvData,
  renderPhotoWhenViewable,
  subtypeId,
  isLargeCard,
  isRatedList,
  isReplaceImage,
  type,
  isMobile,
  isSubtypeExpansionPage,
  dataTrackingParent,
}) {
  const {
    note,
    baseStyleId,
    modelYear,
    slugRankedSubmodel,
    badges,
    modelDisplayOverride,
    rating,
    rank,
    defaultModelYear,
  } = vehicle;
  const { modelSlug, makeSlug, year, makeName, modelName } = modelYear;
  const [checked, setChecked] = useStateCallback(selected);

  function handleChange(event) {
    function callback() {
      onSelectCar(event, { id: baseStyleId, modelYear, styleId: baseStyleId, submodelSlug: slugRankedSubmodel });
    }
    if (selectedCarsNumber >= MAX_CARS_TO_COMPARE && !checked) {
      callback();
      return;
    }
    setChecked(!checked, () => {
      callback();
    });
  }

  useEffect(() => {
    setChecked(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const yearIfNotDefaultModelYear = !slugRankedSubmodel && !!defaultModelYear && defaultModelYear === year ? '' : year;
  const coreLink = getCoreUrl({
    makeSlug,
    modelSlug,
    year: yearIfNotDefaultModelYear,
    submodelSlug: slugRankedSubmodel,
  });
  const uniqueId = `${baseStyleId}-${subtypeId}`;
  const headingId = `${uniqueId}-heading`;
  const descriptionId = `${uniqueId}-description`;
  const noteId = `${uniqueId}-${note && note.length}`;
  const widthToFit = isLargeCard ? 552 : 156;
  const heightToFit = isLargeCard ? 412 : 114;

  const vehicleImage = (
    <VehicleImage
      modelYear={modelYear}
      submodelSlug={slugRankedSubmodel}
      renderWhenViewable={renderPhotoWhenViewable}
      imageWrapperClasses={classnames('w-100 h-100', { 'rounded-8': !isLargeCard })}
      widthToFit={widthToFit}
      heightToFit={heightToFit}
      attrWidth={widthToFit}
      attrHeight={heightToFit}
      style={{ aspectRatio: '3/2' }}
      isReplaceImage={isReplaceImage}
    />
  );

  const cardHeader = (
    <CardHeader
      modelYear={modelYear}
      rating={rating}
      isLargeCard={isLargeCard}
      badges={badges}
      coreLink={coreLink}
      headingId={headingId}
      modelDisplayOverride={modelDisplayOverride}
      uniqueId={uniqueId}
      isSubtypeExpansionPage={isSubtypeExpansionPage}
    />
  );

  const makeModelName = `${makeName} ${modelDisplayOverride || modelName}`;

  const cardDetails = (
    <CardDetails
      vehicle={vehicle}
      coreLink={coreLink}
      showRank={showRank}
      showEvData={showEvData}
      badges={badges}
      headingId={headingId}
      uniqueId={uniqueId}
      isLargeCard={isLargeCard}
      descriptionId={descriptionId}
    >
      {baseStyleId ? (
        <Checkbox
          id={noteId}
          labelText="Compare"
          containerClassName="compare-checkbox pos-r text-cool-gray-30 size-16 mt-1 text-end"
          // label classname used by delighted to hide the modal
          labelClassName="compare-label"
          customLabelClassName="rounded"
          textLabelClassName="ps-1_5"
          onChange={handleChange}
          checked={checked}
          autoComplete="off"
          data-hidedelightedoninteraction
          uniqueLabelDescription={makeModelName}
        >
          {!!selectedCarsNumber && (
            <ScrollLink
              className="skip-link p-0 ms-0_5 btn btn-link text-transform-none visually-hidden-focusable"
              to={COMPARE_VEHICLE_TRAY_ID}
              noScroll
            >
              Skip to compare selected
            </ScrollLink>
          )}
        </Checkbox>
      ) : (
        <div className="compare-checkbox text-cool-gray-50 size-16 mt-1 text-end" style={{ height: '22px' }}>
          Compare N/A
        </div>
      )}
    </CardDetails>
  );

  const showEtrCta =
    isLargeCard &&
    rank === 1 &&
    type === 'hybrid' &&
    modelYear.makeName.toLowerCase() === 'honda' &&
    modelDisplayOverride.toLowerCase() === 'civic hybrid';

  const renderEtrCTA = classes => (
    <div
      className={classnames('py-0_5 ps-0_75 pe-1 fw-normal text-white bg-blue-30', classes)}
      style={{
        borderRadius: '20px',
        textTransform: 'none',
      }}
    >
      <img src={hondaLogo} alt="" width={28} height={18} loading="lazy" className="pe-0_5" />
      <span className="ps-0_5 d-inline-block" style={{ borderLeft: '1px solid #fff' }}>
        Shop Now at ShopHonda.com
      </span>
      <i className="icon-open_in_new medium my-0_25 mx-0_25 size-12" aria-hidden />
      <sup className="size-9" style={{ top: '-0.6rem' }}>
        Ad
      </sup>
    </div>
  );

  const renderDefaultLargeCard = (
    <div className="d-flex flex-column flex-lg-row">
      <figure className="mb-0 jelly-container">{vehicleImage}</figure>
      <div className="card-details-container py-1 px-1 d-flex flex-column">
        {cardHeader}
        {cardDetails}
      </div>
    </div>
  );

  const renderLargeCard = () => {
    if (showEtrCta) {
      return (
        <div className="d-flex flex-column flex-lg-row">
          <Button
            tag="a"
            className="mb-0 p-0 jelly-container pos-r"
            href={HONDA_CLICK_TAG[isMobile]}
            target="_blank"
            rel="noopener noreferrer"
            data-tracking-id="view_content"
            data-tracking-value={HONDA_CLICK_TAG[isMobile]}
          >
            {vehicleImage}
            <div className="pos-a top-0 right-0 hidden-md-up">
              {renderEtrCTA('size-12 me-0_25 me-sm-0_75 mt-0_25 mt-sm-0_75')}
            </div>
            <div className="pos-a top-0 right-0 hidden-lg-up hidden-md-down">
              {renderEtrCTA('size-14 me-0_25 me-sm-0_75 mt-0_25 mt-sm-0_75')}
            </div>
            <div className="pos-a bottom-0 left-0 hidden-lg-down">{renderEtrCTA('size-14 ms-1_75 mb-1_75')}</div>
          </Button>
          <div className="card-details-container py-1 px-1 pos-r d-flex flex-column">
            {cardHeader}
            {cardDetails}
          </div>
          <div>
            <AdUnit {...ETR_ADS_PROPS[isMobile]} position={'1'} renderWhenViewable={false} oneByOne />
          </div>
        </div>
      );
    }
    return renderDefaultLargeCard;
  };

  return (
    <div
      className={classnames('ranking-vehicle-card pos-r mx-auto h-100', {
        'is-large-card': isLargeCard,
      })}
    >
      {showRank && isLargeCard && (
        <div
          className="pos-a pe-none"
          style={{
            zIndex: '1',
            ...(rank === 1 ? { top: '-0.5rem', left: '0.75rem', width: '76px', height: '82px' } : {}),
          }}
        >
          {rank === 1 && (
            <svg viewBox="0 0 76 82" width={76} className="pos-a">
              <use xlinkHref="#winner-flag" />
            </svg>
          )}
          <div
            className={classnames('rank pos-r text-cool-gray-100', {
              'size-36 ps-1_25  pt-1_5 w-100 h-100': rank === 1,
              'size-30 p-1 bg-blue-50': rank > 1,
            })}
          >
            {getOrdinalNumber(rank)}
          </div>
        </div>
      )}
      {showRank && !isLargeCard && (
        <div className="pos-a pe-none rank heading-4 bg-blue-40 text-cool-gray-100 p-0_5 text-shadow-contrast">
          {getOrdinalNumber(rank)}
        </div>
      )}
      <div
        className={classnames('vehicle-card rounded-12 h-100 d-flex flex-column', {
          'w-100 pt-2 pb-1_25 px-1': !isLargeCard,
        })}
        aria-labelledby={headingId}
        aria-describedby={descriptionId}
      >
        {isLargeCard ? (
          <FeatureFlag name="enableEtrRankingsAd">
            {enabled => (enabled ? renderLargeCard() : renderDefaultLargeCard)}
          </FeatureFlag>
        ) : (
          <div className={classnames('d-flex flex-column flex-fill', { 'pt-1': isRatedList })}>
            <div className="d-flex">
              <figure className="w-50 mb-0 pe-0_5">{vehicleImage}</figure>
              <div className="w-50 ps-0_5">{cardHeader}</div>
            </div>
            {cardDetails}
          </div>
        )}
        <LeaseDealsHiddenGemsLink
          vehicle={vehicle}
          type={type}
          className={classnames('card-lease-deals-hidden-gems-link', { 'mt-1': !isLargeCard })}
          dataTrackingParent={dataTrackingParent}
        />
      </div>
    </div>
  );
}

RankingsVehicleCard.propTypes = {
  onSelectCar: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  selectedCarsNumber: PropTypes.number.isRequired,
  vehicle: TypeRankingsEntities.RankingVehicle.isRequired,
  showRank: PropTypes.bool,
  showEvData: PropTypes.bool,
  renderPhotoWhenViewable: PropTypes.bool,
  subtypeId: PropTypes.number.isRequired,
  isLargeCard: PropTypes.bool,
  isRatedList: PropTypes.bool,
  isReplaceImage: PropTypes.bool,
  type: PropTypes.string,
  isMobile: PropTypes.bool,
  isSubtypeExpansionPage: PropTypes.bool,
  dataTrackingParent: PropTypes.string,
  isFirstRankCard: PropTypes.bool,
};

RankingsVehicleCard.defaultProps = {
  showRank: true,
  showEvData: false,
  renderPhotoWhenViewable: false,
  isLargeCard: false,
  isRatedList: true,
  isLazyImage: false,
  isReplaceImage: false,
  type: null,
  isMobile: false,
  isSubtypeExpansionPage: false,
  dataTrackingParent: '',
  isFirstRankCard: false,
};
